import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import { useObjectVal } from 'react-firebase-hooks/database';
import { Alert, Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { getAuth } from 'firebase/auth';
import {
  limitToLast,
  orderByChild,
  query,
  serverTimestamp,
  update,
} from 'firebase/database';
import { AbilityContext } from '../../auth/Can';
import { Loader } from '../../components/Loader';
import {
  getMarkdownSafetyError,
  MarkdownEditor,
  MarkdownRenderer,
  MARKDOWN_MAX_LENGTH,
} from '../../components/markdown';
import { logger } from '../../logging';
import { getDAMApp } from '../../services/firebase';
import { ref } from '../../utils/firebase';
import {
  CURRENT_RELEASE_NOTES_KEY,
  CURRENT_RELEASE_NOTES_PATH,
  RELEASE_VERSION,
} from '../../releaseVersion';
import {
  getReleaseNoteEntries,
  findReleaseNoteEntry,
  getReleaseNotePath,
  MAX_LOADED_RELEASE_NOTES,
  ReleaseNote,
  ReleaseNoteEntry,
  ReleaseNotesByVersion,
} from './releaseNotesModel';

const pagesStyles = require('../pages.css');
const releaseNotesStyles = require('./release-notes.css');

const formatUpdatedAt = (updatedAt?: number) =>
  updatedAt ? new Date(updatedAt).toLocaleString() : '';

export const ReleaseNotes = () => {
  const ability = useAbility(AbilityContext);
  const canEdit = ability.can('manage', 'release_notes');
  const releaseNotesRef = query(
    ref('release_notes'),
    orderByChild('updated_at'),
    limitToLast(MAX_LOADED_RELEASE_NOTES)
  );
  const currentReleaseNotesRef = ref(CURRENT_RELEASE_NOTES_PATH);
  const [releaseNotes, historyLoading, historyLoadError] = useObjectVal(
    releaseNotesRef as any
  ) as [ReleaseNotesByVersion | undefined, boolean, Error | undefined];
  const [currentReleaseNote, currentLoading, currentLoadError] = useObjectVal(
    currentReleaseNotesRef as any
  ) as [ReleaseNote | null | undefined, boolean, Error | undefined];
  const [selectedReleaseKey, setSelectedReleaseKey] = useState(
    CURRENT_RELEASE_NOTES_KEY
  );
  const [selectedReleaseSnapshot, setSelectedReleaseSnapshot] = useState<
    ReleaseNoteEntry | undefined
  >();
  const [optimisticRelease, setOptimisticRelease] = useState<
    ReleaseNoteEntry | undefined
  >();
  const [editBaseline, setEditBaseline] = useState<
    Pick<ReleaseNoteEntry, 'key' | 'notes'> | undefined
  >();
  const [draftNotes, setDraftNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSucceeded, setSaveSucceeded] = useState(false);

  const entries = useMemo(
    () =>
      getReleaseNoteEntries(
        releaseNotes,
        CURRENT_RELEASE_NOTES_KEY,
        RELEASE_VERSION,
        currentReleaseNote
      ),
    [currentReleaseNote, releaseNotes]
  );

  const selectableEntries = useMemo(() => {
    const selectedEntryIsLoaded = findReleaseNoteEntry(
      entries,
      selectedReleaseKey
    );

    if (
      !selectedEntryIsLoaded &&
      selectedReleaseSnapshot?.key === selectedReleaseKey
    ) {
      return [...entries, selectedReleaseSnapshot];
    }

    return entries;
  }, [entries, selectedReleaseKey, selectedReleaseSnapshot]);

  const loadedSelectedRelease =
    findReleaseNoteEntry(selectableEntries, selectedReleaseKey) || entries[0];
  const selectedRelease =
    optimisticRelease?.key === selectedReleaseKey
      ? optimisticRelease
      : loadedSelectedRelease;
  const selectedNotes = selectedRelease.notes;
  const loadError = historyLoadError || currentLoadError;

  useEffect(() => {
    if (!optimisticRelease) {
      return;
    }

    const liveRelease = findReleaseNoteEntry(entries, optimisticRelease.key);
    if (
      liveRelease?.version === optimisticRelease.version &&
      liveRelease.notes === optimisticRelease.notes
    ) {
      setOptimisticRelease(undefined);
    }
  }, [entries, optimisticRelease]);

  useEffect(() => {
    if (!isEditing) {
      setDraftNotes(selectedNotes);
    }
  }, [selectedNotes, isEditing]);

  const selectRelease = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isSaving) {
      return;
    }

    const nextRelease = findReleaseNoteEntry(
      selectableEntries,
      event.currentTarget.value
    );

    if (!nextRelease || nextRelease.key === selectedReleaseKey) {
      return;
    }

    const hasUnsavedChanges = isEditing && draftNotes !== selectedRelease.notes;
    if (
      hasUnsavedChanges &&
      !window.confirm(
        `Discard unsaved changes for v${selectedRelease.version}?`
      )
    ) {
      return;
    }

    setSelectedReleaseKey(nextRelease.key);
    setSelectedReleaseSnapshot(nextRelease);
    setOptimisticRelease(undefined);
    setEditBaseline(
      isEditing ? { key: nextRelease.key, notes: nextRelease.notes } : undefined
    );
    setDraftNotes(nextRelease.notes);
    setSaveError('');
    setSaveSucceeded(false);
  };

  const beginEditing = () => {
    setDraftNotes(selectedNotes);
    setEditBaseline({ key: selectedRelease.key, notes: selectedNotes });
    setSaveError('');
    setSaveSucceeded(false);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraftNotes(selectedNotes);
    setEditBaseline(undefined);
    setSaveError('');
    setIsEditing(false);
  };

  const save = async () => {
    if (ability.cannot('manage', 'release_notes')) {
      setSaveError('You do not have permission to edit release notes.');
      return;
    }

    if (
      !editBaseline ||
      editBaseline.key !== selectedRelease.key ||
      editBaseline.notes !== selectedRelease.notes
    ) {
      setSaveError(
        `Release notes for v${selectedRelease.version} changed while you were editing. Cancel and reopen the editor to load the latest version.`
      );
      return;
    }

    const markdownSafetyError = getMarkdownSafetyError(draftNotes);
    if (markdownSafetyError) {
      setSaveError(markdownSafetyError);
      return;
    }

    setIsSaving(true);
    setSaveError('');
    setSaveSucceeded(false);

    const releaseToSave = selectedRelease;
    const notesToSave = draftNotes;

    try {
      const currentUser = getAuth(getDAMApp()).currentUser;
      await update(ref(getReleaseNotePath(releaseToSave)), {
        version: releaseToSave.version,
        notes: notesToSave,
        updated_at: serverTimestamp(),
        updated_by: currentUser?.email || currentUser?.uid || 'unknown',
      });
      const savedRelease = { ...releaseToSave, notes: notesToSave };
      setSelectedReleaseSnapshot(savedRelease);
      setOptimisticRelease(savedRelease);
      setEditBaseline(undefined);
      setIsEditing(false);
      setSaveSucceeded(true);
    } catch (error) {
      logger.error(`[ReleaseNotes] Unable to save release notes: ${error}`);
      setSaveError('Unable to save release notes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (historyLoading || currentLoading) {
    return <Loader loading={true} />;
  }

  return (
    <div className={pagesStyles.container}>
      <Row className={pagesStyles.headerRow}>
        <Col sm={{ size: 12 }} className={pagesStyles.noPadding}>
          <h3 className={releaseNotesStyles.releaseHeader}>Release Notes</h3>
          <p className={releaseNotesStyles.currentVersion}>
            Current application version: v{RELEASE_VERSION}
          </p>
        </Col>
      </Row>

      {loadError && <Alert color="danger">Unable to load release notes.</Alert>}
      {saveError && <Alert color="danger">{saveError}</Alert>}
      {saveSucceeded && (
        <Alert color="success">
          Release notes for v{selectedRelease.version} saved successfully.
        </Alert>
      )}

      {!loadError && (
        <div className={releaseNotesStyles.releaseControls}>
          <FormGroup className={releaseNotesStyles.releaseSelector}>
            <Label for="release-version-select">Release version</Label>
            <Input
              id="release-version-select"
              name="releaseVersion"
              type="select"
              value={selectedRelease.key}
              disabled={isSaving}
              aria-describedby="release-version-help"
              onChange={selectRelease}
            >
              {selectableEntries.map((entry) => (
                <option value={entry.key} key={entry.key}>
                  v{entry.version}
                  {entry.key === CURRENT_RELEASE_NOTES_KEY ? ' (current)' : ''}
                </option>
              ))}
            </Input>
            <small
              id="release-version-help"
              className={releaseNotesStyles.selectorHelp}
            >
              Choose the release notes to {isEditing ? 'edit' : 'view'}.
            </small>
          </FormGroup>

          {!isEditing && canEdit && (
            <Button color="primary" onClick={beginEditing}>
              Edit v{selectedRelease.version}
            </Button>
          )}
        </div>
      )}

      {!loadError && isEditing && (
        <React.Fragment>
          <FormGroup>
            <Label for="release-notes-editor">
              Release notes for v{selectedRelease.version}
            </Label>
            <MarkdownEditor
              key={selectedRelease.key}
              id="release-notes-editor"
              value={draftNotes}
              onChange={setDraftNotes}
              disabled={isSaving}
              maxLength={MARKDOWN_MAX_LENGTH}
              placeholder="Describe what changed in this release…"
            />
          </FormGroup>
          <div className={releaseNotesStyles.actions}>
            <Button color="primary" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={cancelEditing} disabled={isSaving}>
              Cancel
            </Button>
          </div>
        </React.Fragment>
      )}

      {!loadError && !isEditing && (
        <section
          className={releaseNotesStyles.release}
          key={selectedRelease.key}
        >
          <h4>v{selectedRelease.version}</h4>
          <div className={releaseNotesStyles.notes}>
            {selectedRelease.notes ? (
              <MarkdownRenderer markdown={selectedRelease.notes} />
            ) : (
              <span className={releaseNotesStyles.emptyNotes}>
                No release notes have been published for this version.
              </span>
            )}
          </div>
          {(selectedRelease.updated_by || selectedRelease.updated_at) && (
            <small className={releaseNotesStyles.metadata}>
              Last updated
              {selectedRelease.updated_by
                ? ` by ${selectedRelease.updated_by}`
                : ''}
              {selectedRelease.updated_at
                ? ` on ${formatUpdatedAt(selectedRelease.updated_at)}`
                : ''}
            </small>
          )}
        </section>
      )}
    </div>
  );
};
